import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { teamsApi, fieldOptionsApi } from '../services/api';
import { getOperatorPropertyType } from '../utils/propertyTypes';

const OPERATOR_MAP = {
  EQ: 'equal',
  NEQ: 'notEqual',
  IN: 'in',
  NOT_IN: 'notIn', // adjust if you prefer 'doesNotContain'
  GT: 'greaterThan',
  LT: 'lessThan',
  GTE: 'greaterThanInclusive',
  LTE: 'lessThanInclusive',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'doesNotContain',
  IS_EMPTY: 'isEmpty',
  IS_NOT_EMPTY: 'isNotEmpty',
};

const allowedOperatorLabels = [
  "is equal to",
  "is not equal to",
  "is less than",
  "is less than or equal to",
  "is greater than",
  "is greater than or equal to",
  "is in",
  "is not in",
  "contains",
  "does not contain",
  "is empty",
  "is not empty",
];
const allowedOperatorLabelSet = new Set(
  allowedOperatorLabels.map((label) => label.toLowerCase())
);

const VALUELESS_OPERATORS = new Set(['IS_EMPTY', 'IS_NOT_EMPTY']);


export default function TeamRules() {
  const { id } = useParams(); // team DB primary key
  const teamId = id;
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newMatchType, setNewMatchType] = useState('all');

  const [showAddConditionModal, setShowAddConditionModal] = useState(false);
  const [currentRuleForCondition, setCurrentRuleForCondition] = useState(null);
  const [newCondition, setNewCondition] = useState({ field_name: '', operator: '', value: '' });

  // State for dynamic field options
  const [properties, setProperties] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  useEffect(() => {
    const loadTeamAndRules = async () => {
      try {
        setLoading(true);
        const response = await teamsApi.fetchTeamById(teamId);
        const teamData = response.team;

        if (!teamData?.hs_team_id) {
          throw new Error(`Missing hs_team_id for team ${teamId}`);
        }

        setTeam(teamData);
        console.log('🔍 Team loaded:', teamData);
        const ruleData = await teamsApi.fetchTeamRules(teamData.hs_team_id);
        setRules(ruleData.rules || []);
      } catch (err) {
        console.error('❌ Error loading team or rules:', err);
        setError('Failed to load team rules.');
      } finally {
        setLoading(false);
      }
    };

    if (teamId) loadTeamAndRules();
  }, [teamId]);

  const toggleRuleExpand = (ruleId) => {
    setExpandedRuleId(expandedRuleId === ruleId ? null : ruleId);
  };

  const handleBack = () => navigate(`/teams/${teamId}`);

  const handleCreateRule = async () => {

    console.log('🧪 Creating rule for team:', team);

    if (!newRuleName.trim()) {
      alert('Please enter a rule name');
      return;
    }

    if (!team?.hs_team_id) {
      alert('Team info not yet loaded');
      return;
    }

    console.log('📤 Rule payload:', {
      hs_team_id: team?.hs_team_id,
      hs_portal_id: team?.hs_portal_id,
      rule_name: newRuleName,
      match_type: newMatchType,
    });

    try {
      const created = await teamsApi.createTeamRule({
        hs_team_id: team.hs_team_id,
        hs_portal_id: team.hs_portal_id,
        rule_name: newRuleName,
        match_type: newMatchType,
      });

      setRules([...rules, created.rule]);
      setShowAddRuleModal(false);
      setNewRuleName('');
      setNewMatchType('all');
    } catch (err) {
      console.error('❌ Error creating rule:', err);
      alert('Failed to create rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      await teamsApi.deleteTeamRule(ruleId);
      setRules(rules.filter((r) => r.id !== ruleId));
    } catch (err) {
      console.error('❌ Error deleting rule:', err);
      alert('Failed to delete rule');
    }
  };

  const handleCreateCondition = async () => {
    const { field_name, operator, value } = newCondition;
    if (!field_name.trim() || !operator.trim()) {
      alert('Please complete all fields for the condition.');
      return;
    }
    const mappedOperator = OPERATOR_MAP[operator] || operator;
    console.log(`🔄 Mapping operator '${operator}' → '${mappedOperator}'`);

    console.log('📤 Adding condition:', {
      team: team?.hs_team_id,
      portal: team?.hs_portal_id,
      rule: currentRuleForCondition,
      //condition: newCondition,
      condition: { ...newCondition, operator: mappedOperator },
      object_type: team?.object_type,
      property_type: selectedProperty?.dataType,
    });
    
  
    try {
      const created = await teamsApi.addConditionToRule(currentRuleForCondition, {
        hs_team_id: team.hs_team_id,
        hs_portal_id: team.hs_portal_id,
        object_type: team.object_type,
        field_name,
        operator: mappedOperator,
        //operator,
        value: VALUELESS_OPERATORS.has(operator) ? '' : value,
        property_type: selectedProperty?.dataType || 'string',
      });
  
      // Normalize response so UI renders correctly immediately
    const normalized = {
      id: created.id,
      field_name: created.property || field_name,
      operator: created.operator || operator,
      value: created.property_value || value,
    };

    const updatedRules = rules.map((rule) =>
      rule.id === currentRuleForCondition
        ? { ...rule, conditions: [...(rule.conditions || []), normalized] }
        : rule
    );
  
      setRules(updatedRules);
      setShowAddConditionModal(false);
      setNewCondition({ field_name: '', operator: '', value: '' });
      setSelectedProperty(null);
      setProperties([]);
      setOperators([]);
    } catch (err) {
      console.error('❌ Error adding condition:', err);
      alert('Failed to add condition');
    }
  };
  

  const handleDeleteCondition = async (ruleId, conditionId) => {
    if (!window.confirm('Delete this condition?')) return;

    try {
      await teamsApi.deleteConditionFromRule(ruleId, conditionId);
      const updatedRules = rules.map((rule) =>
        rule.id === ruleId
          ? { ...rule, conditions: rule.conditions.filter((c) => c.id !== conditionId) }
          : rule
      );
      setRules(updatedRules);
    } catch (err) {
      console.error('❌ Error deleting condition:', err);
      alert('Failed to delete condition');
    }
  };

  // Function to fetch properties when Add Condition modal opens
  const fetchPropertiesForTeam = async () => {
    if (!team?.object_type) {
      console.error('No object_type found for team');
      return;
    }

    setLoadingProperties(true);
    try {
      const response = await fieldOptionsApi.fetchProperties(team.object_type, team.hs_portal_id);
      const sortedProps = (response.properties || []).sort((a, b) =>
        a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
      );
      setProperties(sortedProps);
    } catch (err) {
      console.error('Error fetching properties:', err);
      alert('Failed to load field options. Please try again.');
      setProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Function to fetch operators when property is selected
  const fetchOperatorsForProperty = async (property) => {
    const operatorPropertyType = getOperatorPropertyType(property);
    if (!operatorPropertyType) return;

    console.log('🔍 Fetching operators for property:', property);

    setLoadingOperators(true);
    try {
      const response = await fieldOptionsApi.fetchOperators(operatorPropertyType);
      console.log('🔍 Operators fetched:', response.operators);
      setOperators(response.operators || []);
      console.log("Operators:", operators);
    } catch (err) {
      console.error('Error fetching operators:', err);
      alert('Failed to load operators. Please try again.');
      setOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  };

  // Handle property selection
  const handlePropertyChange = (propertyName) => {
    const property = properties.find(p => p.name === propertyName);
    setSelectedProperty(property);
    setNewCondition({ 
      ...newCondition, 
      field_name: propertyName,
      operator: '', // Reset operator when property changes
      value: ''
    });
    
    if (property) {
      fetchOperatorsForProperty(property);
    } else {
      setOperators([]);
    }
  };

  // Handle operator selection
  const handleOperatorChange = (operatorValue) => {
    setNewCondition({
      ...newCondition,
      operator: operatorValue,
      value: VALUELESS_OPERATORS.has(operatorValue) ? '' : newCondition.value
    });
  };

  // Reset modal state when opening
  const handleOpenAddConditionModal = (ruleId) => {
    setCurrentRuleForCondition(ruleId);
    setNewCondition({ field_name: '', operator: '', value: '' });
    setSelectedProperty(null);
    setProperties([]);
    setOperators([]);
    setShowAddConditionModal(true);
    fetchPropertiesForTeam();
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Team Rules</h1>
        </div>
        <button
          onClick={() => setShowAddRuleModal(true)}
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md"
        >
          <FiPlus className="w-4 h-4 mr-2" /> Add Rule
        </button>
      </div>

      {/* Add Rule Modal */}
      {showAddRuleModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add New Rule</h2>

            <label className="block mb-2 text-sm">Rule Name</label>
            <input
              type="text"
              value={newRuleName}
              onChange={(e) => setNewRuleName(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
            />

            <label className="block mb-2 text-sm">Match Type</label>
            <select
              value={newMatchType}
              onChange={(e) => setNewMatchType(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
            >
              <option value="all">Match ALL Conditions</option>
              <option value="any">Match ANY Condition</option>
            </select>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowAddRuleModal(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRule}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Condition Modal */}
      {showAddConditionModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add Condition</h2>

            <label className="block mb-2 text-sm">Field Name</label>
            {loadingProperties ? (
              <div className="w-full px-3 py-2 border rounded mb-4 text-gray-500">
                Loading properties...
              </div>
            ) : (
              <select
                value={newCondition.field_name}
                onChange={(e) => handlePropertyChange(e.target.value)}
                className="w-full px-3 py-2 border rounded mb-4"
                disabled={properties.length === 0}
              >
                <option value="">Select a field</option>
                {properties.map((property) => (
                  <option key={property.name} value={property.name}>
                    {property.label} ({property.name})
                  </option>
                ))}
              </select>
            )}

            <label className="block mb-2 text-sm">Operator</label>

            {loadingOperators ? (
              <div className="w-full px-3 py-2 border rounded mb-4 text-gray-500">
                Loading operators...
              </div>
            ) : (
              <select
                value={newCondition.operator}
                onChange={(e) => handleOperatorChange(e.target.value)}
                className="w-full px-3 py-2 border rounded mb-4"
                disabled={operators.length === 0 || !selectedProperty}
              >
                <option value="">Select an operator</option>

                {operators
                  .filter((op) => allowedOperatorLabelSet.has(String(op.label || '').toLowerCase()))
                  .map((operator) => (
                    <option key={operator.value} value={operator.value}>
                      {operator.label}
                    </option>
                  ))}
              </select>
            )}

            {!VALUELESS_OPERATORS.has(newCondition.operator) && (
              <>
                <label className="block mb-2 text-sm">Value</label>
                {selectedProperty?.options?.length > 0 ? (
                  // Show dropdown for enumeration properties
                  <select
                    value={newCondition.value}
                    onChange={(e) => setNewCondition({ ...newCondition, value: e.target.value })}
                    className="w-full px-3 py-2 border rounded mb-4"
                  >
                    <option value="">Select a value</option>
                    {selectedProperty.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Show text input for other property types
                  <input
                    type="text"
                    value={newCondition.value}
                    onChange={(e) => setNewCondition({ ...newCondition, value: e.target.value })}
                    className="w-full px-3 py-2 border rounded mb-4"
                    placeholder="Enter value"
                  />
                )}
              </>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowAddConditionModal(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCondition}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={!newCondition.field_name || !newCondition.operator}
              >
                Add Condition
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules UI */}
      {loading ? (
        <p className="text-gray-600 text-center">Loading rules...</p>
      ) : error ? (
        <p className="text-red-600 text-center">{error}</p>
      ) : rules.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-700">No rules configured yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule) => (
            <div key={rule.id} className="border rounded-lg shadow-sm bg-white">
              <div className="flex justify-between items-center px-4 py-3 hover:bg-gray-50">
                <div onClick={() => toggleRuleExpand(rule.id)} className="cursor-pointer flex-1">
                  <h3 className="text-lg font-semibold">{rule.rule_name || `Rule #${rule.id}`}</h3>
                  <p className="text-sm text-gray-600">
                    Match Type:{' '}
                    <span className="font-medium">
                      {rule.rule_condition === 'any' ? 'Match ANY Condition' : 'Match ALL Conditions'}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteRule(rule.id)}
                  className="p-2 text-red-600 hover:text-red-800"
                >
                  <FiTrash2 />
                </button>
              </div>

              {expandedRuleId === rule.id && (
                <div className="px-4 py-3 border-t bg-gray-50">
                  {rule.conditions?.length > 0 ? (
                    <ul className="space-y-2 mb-3">
                      {rule.conditions.map((cond) => (
                        <li key={cond.id} className="p-3 bg-white border rounded flex justify-between">
                          <span>
                            <strong>Field:</strong> {cond.field_name} | <strong>Operator:</strong> {cond.operator} |{' '}
                            <strong>Value:</strong> {cond.value}
                          </span>
                          <button
                            onClick={() => handleDeleteCondition(rule.id, cond.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FiTrash2 />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600 mb-3">No conditions defined for this rule.</p>
                  )}
                  <button
                    onClick={() => handleOpenAddConditionModal(rule.id)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm"
                  >
                    <FiPlus className="inline w-4 h-4 mr-1" /> Add Condition
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

